import { api } from './api';

export type LiderResumo = { id: number; nome: string; telefone: string | null };

export type EquipeResumo = {
  id: number;
  nome: string;
  chave: string;
  observacao: string | null;
  ativo: boolean;
  lideres: LiderResumo[];
  pessoas: number;
};

export type PessoaDaEquipe = {
  id: number;
  nome: string;
  telefone: string | null;
  email: string | null;
  subId: number | null;
  lidera: boolean;
};

export const buscarEquipes = () =>
  api<{ equipes: EquipeResumo[]; podeEditar: boolean }>('/equipes');

export const buscarEquipe = (id: number) =>
  api<{
    equipe: Omit<EquipeResumo, 'lideres' | 'pessoas'>;
    podeEditarEquipe: boolean;
    podeMexerNosSubs: boolean;
    meusSubs: number[];
    souLider: boolean;
    subs: Sub[];
    pessoas: PessoaDaEquipe[];
  }>(`/equipes/${id}`);

export const criarEquipe = (nome: string) =>
  api<{ id: number }>('/equipes', { method: 'POST', body: JSON.stringify({ nome }) });

export const renomearEquipe = (id: number, nome: string) =>
  api(`/equipes/${id}`, { method: 'PATCH', body: JSON.stringify({ nome }) });

export const incluirNaEquipe = (
  id: number,
  dados: {
    nome?: string;
    telefone?: string | null;
    membroId?: number;
    lider?: boolean;
    subId?: number | null;
  },
) => api(`/equipes/${id}/pessoas`, { method: 'POST', body: JSON.stringify(dados) });

export const criarSub = (id: number, nome: string, lideres: string[] = []) =>
  api<{ id: number }>(`/equipes/${id}/subs`, {
    method: 'POST',
    body: JSON.stringify(lideres.length ? { nome, lideres } : { nome }),
  });

export const incluirLiderDoSub = (id: number, subId: number, nome: string) =>
  api(`/equipes/${id}/subs/${subId}/lideres`, { method: 'POST', body: JSON.stringify({ nome }) });

export const trocarDeSub = (id: number, membroId: number, subId: number | null) =>
  api(`/equipes/${id}/pessoas/${membroId}/sub`, {
    method: 'PUT',
    body: JSON.stringify({ subId }),
  });

export const tirarDaEquipe = (id: number, membroId: number) =>
  api(`/equipes/${id}/pessoas/${membroId}`, { method: 'DELETE' });

/** So numeros, para montar o link do WhatsApp. */
export const soDigitos = (t: string) => t.replace(/\D/g, '');

export type Culto = { chave: string; nome: string };

export type PessoaNaChamada = {
  id: number;
  nome: string;
  telefone: string | null;
  subId: number | null;
  subNome: string | null;
  presencas: Record<string, boolean>;
};

export const buscarChamada = (id: number, semana?: string) =>
  api<{
    semana: string;
    cultos: Culto[];
    pessoas: PessoaNaChamada[];
    totais: Record<string, number>;
    quantasPessoas: number;
  }>(`/equipes/${id}/presenca${semana ? `?semana=${semana}` : ''}`);

/** Marca UMA pessoa em UM culto — nunca a tela inteira. */
export const marcarPresenca = (
  id: number,
  dados: { membroId: number; semana: string; culto: string; presente: boolean },
) => api(`/equipes/${id}/presenca`, { method: 'PUT', body: JSON.stringify(dados) });

export const resumoDaSemana = () =>
  api<{
    semana: string;
    cultos: Culto[];
    equipes: { id: number; nome: string; pessoas: number; presentes: Record<string, number> }[];
  }>('/equipes/resumo-semana');

export type ExtrasDoPlacar = {
  sem: number;
  arena: number;
  dom: number;
  novos: number;
  visita: number;
  iv: number;
  celulas: number;
  pdSem: number;
  pdTotal: number;
  revisaoSem: number;
  revisaoTotal: number;
  discipulado: number;
};

export type LinhaDoPlacar = {
  id: number;
  nome: string;
  equipe?: string;
  liderNome?: string | null;
  extrasSemana?: ExtrasDoPlacar;
  extrasMes?: ExtrasDoPlacar;
  presencasSemana: number;
  pessoasSemana: number;
  presencasMes: number;
  pessoasMes: number;
  pontosSemana: number;
  pontosMes: number;
  posicaoSemana: number;
  posicaoMes: number;
};

export type Sub = {
  id: number;
  nome: string;
  chave: string;
  liderId: number | null;
  liderNome: string | null;
  lideres: { id: number; nome: string }[];
  pessoas: number;
};

export const buscarPlacar = (mes?: string) =>
  api<{
    mes: string;
    semana: string;
    semanas: string[];
    quantasEquipes: number;
    equipes: LinhaDoPlacar[];
    subs: LinhaDoPlacar[];
  }>(`/equipes/placar${mes ? `?mes=${mes}` : ''}`);

export const minhaPosicao = () =>
  api<{
    mes: string;
    semana?: string;
    quantasEquipes?: number;
    quantosSubs?: number;
    equipes: {
      id: number;
      nome: string;
      posicaoSemana: number;
      posicaoMes: number;
      pontosSemana: number;
      pontosMes: number;
      presencasMes: number;
    }[];
    subs?: {
      id: number;
      nome: string;
      equipe?: string;
      posicaoSemana: number;
      posicaoMes: number;
      pontosSemana: number;
      pontosMes: number;
    }[];
  }>('/equipes/minha-posicao');

export type CampoDoRelatorio = { chave: string; nome: string; tipo: 'gente' | 'dinheiro' };

export type LinhaDoRelatorio = {
  subId: number;
  nome: string;
  membresia: number;
  [campo: string]: number | string;
};

export const buscarRelatorio = (id: number, semana?: string) =>
  api<{
    semana: string;
    campos: CampoDoRelatorio[];
    podeEscrever: boolean;
    subs: LinhaDoRelatorio[];
  }>(`/equipes/${id}/relatorio${semana ? `?semana=${semana}` : ''}`);

/** O Parceiro de Deus da equipe numa semana — o valor que o 12 lança. */
export const buscarPdDaEquipe = (id: number, semana?: string) =>
  api<{ semana: string; valor: number }>(`/equipes/${id}/pd${semana ? `?semana=${semana}` : ''}`);

/** Guarda o Parceiro de Deus da semana. Lançar de novo substitui, nunca soma. */
export const salvarPdDaEquipe = (id: number, dados: { semana: string; valor: number }) =>
  api<{ ok: boolean; semana: string; valor: number }>(`/equipes/${id}/pd`, {
    method: 'PUT',
    body: JSON.stringify(dados),
  });

/** Guarda UM número do relatório — nunca a linha inteira. */
export const salvarNumeroDoRelatorio = (
  id: number,
  subId: number,
  dados: { semana: string; campo: string; valor: number },
) =>
  api<{ membresia: number }>(`/equipes/${id}/subs/${subId}/relatorio`, {
    method: 'PUT',
    body: JSON.stringify(dados),
  });

/** "R$ 1.234,50" a partir de centavos. */
export const emReais = (centavos: number) =>
  (centavos / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
